import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { formatNumber } from '@/lib/format'
import { gsap, useGSAP, MOTION_QUERY } from '@/lib/gsap'

interface CountUpProps {
    value: number
    decimals?: number
    prefix?: string
    suffix?: string
    className?: string
}

const render = (value: number, decimals: number): string =>
    decimals > 0 ? value.toFixed(decimals) : formatNumber(value)

export const CountUp: FC<CountUpProps> = ({
    value,
    decimals = 0,
    prefix = '',
    suffix = '',
    className
}): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)
    const [display, setDisplay] = useState(render(value, decimals))

    useGSAP(() => {
        const element = ref.current

        if (!element) return

        const media = gsap.matchMedia()

        media.add(MOTION_QUERY, () => {
            const counter = { current: 0 }

            setDisplay(render(0, decimals))

            gsap.to(counter, {
                current: value,
                duration: 2.2,
                ease: 'power3.out',
                scrollTrigger: { trigger: element, start: 'top 92%', once: true },
                onUpdate: () => setDisplay(render(counter.current, decimals))
            })

            return () => setDisplay(render(value, decimals))
        })

        return () => media.revert()
    }, { scope: ref })

    return (
        <span ref={ref} className={className}>
            {prefix}{display}{suffix}
        </span>
    )
}
